import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Sparkles, Calendar, CheckCircle2 } from 'lucide-react';

/**
 * Componente que muestra la versión actual y changelog en el Login
 */
export default function VersionInfo() {
  const [changelog, setChangelog] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchChangelog();
  }, []);

  const fetchChangelog = async () => {
    try {
      // Agregar timestamp para evitar caché
      const response = await fetch(`/changelog.json?t=${Date.now()}`);
      if (response.ok) {
        const data = await response.json();
        setChangelog(data);
      }
    } catch (error) {
      console.error('Error cargando changelog:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const opciones = { day: 'numeric', month: 'long', year: 'numeric' };
    return date.toLocaleDateString('es-ES', opciones);
  };

  // Detectar si es Preview o Producción
  const isPreview = () => {
    const hostname = window.location.hostname;
    return hostname === 'localhost' || 
           hostname === '127.0.0.1' ||
           hostname.includes('preview') || 
           hostname.includes('emergentapps.ai') ||
           hostname.includes('emt-');
  };

  if (loading) {
    return (
      <div className="text-center text-gray-400 text-xs mt-6">
        Cargando versión...
      </div>
    );
  }

  if (!changelog) {
    return null;
  }

  const currentVersion = changelog.versions[0];

  return (
    <div className="mt-6 w-full max-w-sm">
      {/* Badge de entorno */}
      {isPreview() && (
        <div className="flex justify-center mb-3">
          <span className="bg-amber-100 text-amber-800 text-xs font-semibold px-3 py-1 rounded-full border border-amber-300">
            🧪 PREVIEW
          </span>
        </div>
      )}
      
      {/* Versión actual - clickeable */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-center group hover:bg-gray-50 rounded-lg py-2 px-3 transition-colors"
      >
        <div className="flex items-center justify-center gap-2 text-gray-500 text-sm">
          <Sparkles className="w-4 h-4 text-emerald-500" />
          <span className="font-medium text-gray-700">
            Versión {changelog.current_version}
          </span>
          <span className="text-gray-400">•</span>
          <span className="text-gray-400 text-xs flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {formatDate(currentVersion.date)}
          </span>
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
          )}
        </div>
      </button>

      {/* Changelog expandido */}
      {expanded && (
        <div className="mt-3 bg-gray-50 rounded-lg p-4 text-left max-h-64 overflow-y-auto border border-gray-200">
          {changelog.versions.slice(0, 3).map((version, idx) => (
            <div key={version.version} className={idx > 0 ? 'mt-4 pt-4 border-t border-gray-200' : ''}>
              <div className="flex items-center gap-2 mb-2">
                <span className="font-bold text-emerald-700 text-sm">
                  v{version.version}
                </span>
                <span className="text-xs text-gray-400">
                  {formatDate(version.date)}
                </span>
              </div>
              <p className="text-xs font-medium text-gray-700 mb-2">
                {version.title}
              </p>
              <ul className="space-y-1">
                {version.changes.map((change, i) => (
                  <li key={i} className="text-xs text-gray-600 flex items-start gap-1">
                    <span>{change}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
